import React from 'react';
import { usePageTitle, useSiteConfig } from '../../config';
import { alpha, styled } from '@mui/material/styles';
import '../../../node_modules/@okta/okta-signin-widget/dist/css/okta-sign-in.min.css';
import { useTranslate } from '@im/react-localization';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useIsDown } from '@im/ui-lib/zenith/responsive';
import { useLocation } from 'react-router';

type Props = {
  children: JSX.Element;
};

export const LoginLayoutX4EC = (props: Readonly<Props>) => {
  const { children } = props;
  const translateStr = useTranslate();
  const copyrightContent = translateStr('OktaWidget.Copyright');
  const whatYearIsItNow = new Date().getFullYear().toString();
  const { updatePageTitle } = usePageTitle();
  const { storefrontInfo } = useSiteConfig();
  const mdBp = useIsDown('md');
  const { pathname } = useLocation();

  const isForgotPassword = pathname === '/forgot-password';
  updatePageTitle(translateStr('Title.Login'));

  return (
    <StyledGridContainer
      columnSpacing={8}
      container
      height="100vh"
      paddingLeft={8}
    >
      <Grid
        alignItems="center"
        display="flex"
        flexDirection="column"
        item
        justifyContent="space-between"
        md={6}
        sx={{
          pb: 8,
          pt: 8,
        }}
        xs={12}
      >
        <Box height={36} width="100%">
          <img
            alt={`${storefrontInfo?.displayName} logo`}
            height="36"
            src={storefrontInfo?.logo ?? '/xshared/assets/img/logo-ingram-blue'}
            width="auto"
          />
        </Box>
        <Box marginTop={12}>
          <Typography variant="display-sm-semibold">
            {isForgotPassword ? 'Password reset' : 'Welcome back'}
          </Typography>
          {children}
        </Box>

        <Box alignItems="flex-end" display="flex" pt={8} width="100%">
          <Typography
            color={(theme) => theme.palette.gray[600]}
            variant="text-sm-regular"
          >
            {copyrightContent.replace(/\{\{year\}\}/gi, whatYearIsItNow)}
          </Typography>
        </Box>
      </Grid>

      {!mdBp && (
        <Grid
          item
          md={6}
          sx={{
            alignItems: 'center',
            display: 'flex',
            pb: 4,
            pr: 4,
            pt: 4,
          }}
          xs={0}
        >
          <Stack
            height="100%"
            maxHeight="1400px"
            pl="10%"
            pr="10%"
            pt="15%"
            sx={{
              backgroundColor: 'primary.600',
              backgroundImage: (theme) =>
                `linear-gradient(45deg, ${alpha(
                  theme.palette.primary[900] ?? '',
                  0.8
                )} 0%, ${alpha(
                  theme.palette.primary[600] ?? '',
                  0.8
                )} 80%), url('/x4ec/assets/img/login/login-splash.jpg')`,
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundSize: 'cover',
              borderRadius: '20px',
              overflow: 'hidden',
            }}
            width="100%"
          >
            <img
              alt="stars icon"
              height={80}
              src="/x4ec/assets/img/login/login-stars.svg"
              width={80}
            />
            <Typography
              color="common.white"
              flexWrap="wrap"
              maxWidth={448}
              mb={6}
              mt={8}
              variant="display-xl-semibold"
            >
              {translateStr('Login.SplashText')}
            </Typography>
            <Typography
              color="primary.100"
              flexWrap="wrap"
              maxWidth={448}
              variant="text-xl-bold"
            >
              {translateStr('Login.SplashSupportingText')}
            </Typography>
          </Stack>
        </Grid>
      )}
    </StyledGridContainer>
  );
};

const StyledGridContainer = styled(Grid)(({ theme }) => ({
  '#okta-sign-in .auth-header': {
    padding: '0',
  },
  '#okta-sign-in .beacon-container': {
    bottom: 50,
  },
  '#okta-sign-in.auth-container': {
    '.mfa-google-auth,.mfa-google-auth-30': {
      backgroundImage:
        'url(/cep/assets/okta-signin-widget/img/icons/mfa/google-ms-authenticators_sm.png)',
      backgroundSize: 'contain',
    },
  },
  '#okta-sign-in.auth-container.main-container': {
    '.auth-content': {
      margin: 0,
      padding: 'unset',
    },
    '.button-primary': {
      background: theme.palette.primary[600],
      borderColor: theme.palette.primary[600],
      borderRadius: 32,
      color: theme.palette.common.white,
      fontSize: 18,
      height: 44,
    },
    '.link': {
      ...theme.typography['text-sm-semibold'],
      color: theme.palette.primary[700],
      textDecoration: 'none',
    },
    '.o-form .infobox': {
      color: theme.palette.text.secondary,
    },
    '.o-form .input-fix': {
      border: 'unset',
      height: 40,
      input: {
        '&:-webkit-autofill': {
          WebkitBoxShadow: `0 0 0 25px ${theme.palette.common.white} inset`,
          WebkitTextFillColor: theme.palette.gray[500],
        },
        '&::selection': {
          //backgroundColor: theme.palette.common.black,
        },
        border: `1px solid ${theme.palette.gray[400]}`,
        borderRadius: 8,
      },
    },
    '.o-form-explain': {
      color: 'unset',
      textAlign: 'left',
    },
    '.o-form-fieldset': {
      '&:first-of-type': {
        marginBottom: 20,
      },
    },
    '.o-form-head': {
      ...theme.typography['text-md-regular'],
      color: theme.palette.gray[600],
      textAlign: 'left',
    },
    '.okta-form-input-field': {
      backgroundColor: 'unset',
      boxShadow: 'unset',
    },
    '.okta-form-label': {
      color: theme.palette.gray[700],
      fontWeight: 500,
      position: 'relative',
      top: -4,
      width: 'auto',
      zIndex: 1,
    },
    '.okta-sign-in-header.auth-header': {
      '.beacon-container.beacon-small': {
        display: 'none',
      },
      borderBottom: 'unset',
    },

    backgroundColor: 'transparent',
    borderColor: 'transparent',
    boxShadow: 'none',
    color: theme.palette.text.primary,
    fontFamily: 'Inter',
    // height: '100vh',
    input: {
      backgroundColor: theme.palette.common.white,
      color: theme.palette.text.primary,
      fontFamily: 'unset',
    },
    margin: '0',
    overflow: 'unset',
  },
  '#okta-sign-in.no-beacon .auth-header': {
    paddingBottom: 'unset',
  },
}));
